import { cUtil }                    from "../../functions/util";
import { cAdminDeliveryItems }      from "./admin-delivery-items";
import { cAdminDeliveryItemsOrder } from "./admin-delivery-items-order";

interface ExtraAmounts {
  net   : number;
  tax   : number;
  total : number;
}

export class cAdminDeliveryItemsExtra {

  tr           = document.createElement('tr');
  td_status    = document.createElement('td');
  td_date      = document.createElement('td');
  td_product   = document.createElement('td');
  td_ordered   = document.createElement('td');
  td_delivered = document.createElement('td');
  td_price     = document.createElement('td');
  td_totals    = document.createElement('td');
  td_note      = document.createElement('td');

  p_name          = document.createElement('p');
  span_ordered    = document.createElement('span');
  input_delivered = document.createElement('input');
  span_unit       = document.createElement('span');
  div_taxRate     = document.createElement('div');
  div_subTotal    = document.createElement('div');
  div_tax         = document.createElement('div');
  div_total       = document.createElement('div');

  data     : DeliveryExtraLine;
  page     : cAdminDeliveryItems;
  order    : cAdminDeliveryItemsOrder;
  isCoupon : boolean;
  saving   = false;
  // A value that came in while the last one was being saved. It is saved right after, otherwise it
  // would only live in the field.
  saveAgain = false;

  // A coupon row or the reserve of the order. On both only Geliefert is entered, net, and with the
  // tax on it makes up the row's Total in the Liefersumme. A coupon row is one tax rate of the order
  // and starts with what it took off the subtotal. The reserve starts with 0: it is not captured,
  // only an amount entered on it is - positive to charge more, negative to give some back.
  constructor(parent: HTMLElement, data: DeliveryExtraLine, page: cAdminDeliveryItems, order: cAdminDeliveryItemsOrder) {

    this.data     = data;
    this.page     = page;
    this.order    = order;
    this.isCoupon = data.type === 'coupon';

    // ---- Structure ----
    parent.append(this.tr);
      this.tr.append(this.td_status);
      this.tr.append(this.td_date);
      this.tr.append(this.td_product);
        this.td_product.append(this.p_name);
      this.tr.append(this.td_ordered);
        this.td_ordered.append(this.span_ordered);
      this.tr.append(this.td_delivered);
        this.td_delivered.append(this.input_delivered);
        this.td_delivered.append(this.span_unit);
      this.tr.append(this.td_price);
      this.tr.append(this.td_totals);
        this.td_totals.append(this.div_taxRate);
        this.td_totals.append(this.div_subTotal);
        this.td_totals.append(this.div_tax);
        this.td_totals.append(this.div_total);
      this.tr.append(this.td_note);

    // ---- Init ----
    this.tr             .classList.add('c-extra');
    this.td_ordered     .classList.add('c-ordered-cell');
    this.td_totals      .classList.add('c-totals');
    this.p_name         .classList.add('c-name');
    this.div_total      .classList.add('c-total');
    this.span_ordered   .classList.add('c-ordered');
    this.input_delivered.classList.add('c-in-amount');
    this.span_unit      .classList.add('c-unit');

    // A coupon is entered on Rappen and takes off, so it is at most 0. The reserve is on 5 Rappen
    // like Vor Steuern of a line, and a negative amount gives part of the order total back.
    this.input_delivered.type  = 'number';
    this.input_delivered.step  = this.isCoupon ? '0.01' : '0.05';
    if (this.isCoupon) this.input_delivered.max = '0';
    this.input_delivered.value = data.delivered;
    this.input_delivered.setAttribute('aria-label', 'Geliefert');

    this.lock();

    // "Gutschein herbstmesse26 (2.6%)": an order with two tax rates has two coupon rows.
    this.p_name      .innerText = this.isCoupon
      ? ['Gutschein', data.name, `(${data.tax_rate}%)`].filter((part) => part !== '').join(' ')
      : data.name;
    // Net, like Geliefert, which starts with it. A coupon of 5.00 split over two tax rates gives
    // -4.19 and -0.81, as the coupon took it off the subtotal.
    this.span_ordered.innerText = `${this.orderedNet().toFixed(2)} CHF`;
    this.span_unit   .innerText = 'CHF';

    this.updateTotals();

    // ---- Events ----
    this.input_delivered.addEventListener('change', () => {
      this.data.delivered = this.input_delivered.value;
      this.updateTotals();
      this.order.updateSum();
      this.save();
    });
  }

  // A captured order stays as wallee got it, the server refuses the save as well (save.php).
  lock(): void {
    if (!this.order.data.captured) return;
    this.input_delivered.disabled = true;
    this.input_delivered.title    = 'Bereits eingezogen';
  }

  orderedNet(): number {
    return Math.round(this.data.ordered_net * 100) / 100;
  }

  // An empty field is an error, the same as on a line. 0 is valid and means "nothing".
  hasError(): boolean {
    if (this.data.delivered === '') return true;
    const value = Number(this.data.delivered);
    if (isNaN(value)) return true;
    return this.isCoupon && value > 0;
  }

  // Vor Steuern, Steuer and Total from what is entered under Geliefert, null while it is not valid.
  amounts(): ExtraAmounts | null {
    if (this.hasError()) return null;
    const value = Number(this.data.delivered);
    const rate  = this.data.tax_rate;

    // Both are entered net. A coupon stays on Rappen, like the coupon amount in woocommerce, the
    // reserve goes on 5 Rappen like Vor Steuern of a line. The tax is calculated from that value and
    // rounded on 5 Rappen like the tax of a line; the reserve itself carries none.
    const net = this.isCoupon ? Math.round(value * 100) / 100 : cUtil.swissRound(value);
    const tax = cUtil.swissRound(net * rate / 100);
    return {net, tax, total: Number((net + tax).toFixed(2))};
  }

  // What the row brings into the Liefersumme.
  total(): number | null {
    return this.amounts()?.total ?? null;
  }

  updateTotals(): void {
    const amounts = this.amounts();

    if (amounts === null) this.input_delivered.classList.add('c-error-outline');
    else                  this.input_delivered.classList.remove('c-error-outline');

    this.div_taxRate  .innerHTML = '';
    this.div_subTotal .innerHTML = '';
    this.div_tax      .innerHTML = '';
    this.div_total    .innerHTML = '';

    this.row(this.div_taxRate , 'Steuersatz' , `${this.data.tax_rate.toFixed(2)}%`);
    this.row(this.div_subTotal, 'Vor Steuern', amounts === null ? '–' : amounts.net  .toFixed(2));
    this.row(this.div_tax     , 'Steuer'     , amounts === null ? '–' : amounts.tax  .toFixed(2));
    this.row(this.div_total   , 'Total'      , amounts === null ? '–' : amounts.total.toFixed(2));
  }

  row(parent: HTMLElement, label: string, value: string): void {
    const span_label = document.createElement('span');
    const span_value = document.createElement('span');
    span_label.innerText = label;
    span_value.innerText = value;
    parent.append(span_label);
    parent.append(span_value);
  }

  // Set by the order when it spreads the coupon anew, and saved like an entry by hand. Without a
  // toast of its own: it follows the line that was just changed, which has one.
  setDelivered(net: number): void {
    const value = net.toFixed(2);
    if (value === this.data.delivered) return;

    this.data.delivered        = value;
    this.input_delivered.value = value;
    this.updateTotals();
    this.save(true);
  }

  async save(quiet = false): Promise<void> {
    if (this.hasError()) return;
    if (this.saving) {
      this.saveAgain = true;
      return;
    }
    this.saving = true;

    const result = this.isCoupon
      ? await this.page.fetchCoupon(this.order.data.order_id, this.data)
      : await this.page.fetchFee(this.data);
    this.saving = false;

    if (this.saveAgain) {
      this.saveAgain = false;
      await this.save(quiet);
      return;
    }

    if (!result.ok) {
      this.page.toast(result.message, true);
      this.input_delivered.classList.add('c-error-outline');
      return;
    }

    if (quiet) return;
    const label = this.isCoupon ? this.p_name.innerText : this.data.name;
    this.page.toast(`${result.message}: ${label}`, false);
  }
}
